import React from 'react';
import PropTypes from 'prop-types';

import { ElementClickTracker } from '../../analytics/cohesion';
import { ELEMENT_TYPES } from '../../analytics/cohesion/events/element-clicked/index';
import Button from '../Shared/Button';
import { filterProvidersWithInvalidEmail } from './validation';

/**
 * @typedef {import('./types').RosterProvider} RosterProvider
 */

/**
 * Displays an error message with consistent styling.
 * @param {{ message: string, testId: string }} props
 */
const ErrorMessage = ({ message, testId }) => {
  if (!message) return null;

  return (
    <p
      className="text-base text-coral-96 font-semibold"
      role="alert"
      data-testid={testId}
    >
      {message}
    </p>
  );
};

ErrorMessage.propTypes = {
  message: PropTypes.string,
  testId: PropTypes.string.isRequired,
};

/**
 * Displays provider count message.
 * @param {{ count: number, testId: string }} props
 */
const ProviderCountMessage = ({ count, testId }) => {
  if (count === 0) return null;

  return (
    <p className="text-[15px] sm:text-base font-semibold" data-testid={testId}>
      {count} {count === 1 ? 'Provider' : 'Providers'} added to Elevated
      Subscription Roster
    </p>
  );
};

ProviderCountMessage.propTypes = {
  count: PropTypes.number.isRequired,
  testId: PropTypes.string.isRequired,
};

/**
 * Displays checkout button with validation and error messages.
 * Owns the logic for determining button state and error display.
 *
 * @param {{
 *   providers: RosterProvider[],
 *   checkingOut: boolean,
 *   checkoutError: string,
 *   onCheckout: () => void
 * }} props
 */
const CheckoutSection = ({
  providers,
  checkingOut,
  checkoutError,
  onCheckout,
}) => {
  const invalidProviderNames = filterProvidersWithInvalidEmail(providers).map(
    (p) => p.displayName,
  );
  const hasProviders = providers.length > 0;
  const hasInvalidProviders = invalidProviderNames.length > 0;

  // Button state
  const isDisabled = !hasProviders || hasInvalidProviders || checkingOut;
  const buttonText = checkingOut ? 'Processing...' : 'Continue to Checkout';

  // Error messages
  const invalidEmailMessage =
    hasInvalidProviders && hasProviders
      ? `Please enter a valid email address for ${invalidProviderNames.join(', ')} before continuing.`
      : '';

  // Show provider count message only when there are providers and no errors
  const showProviderCount =
    hasProviders && !checkoutError && !hasInvalidProviders;

  return (
    <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 md:justify-end">
      <div className="flex flex-col gap-2 md:items-end flex-1">
        <ErrorMessage message={checkoutError} testId="checkout-error-message" />
        <ErrorMessage
          message={invalidEmailMessage}
          testId="invalid-providers-error-message"
        />
        {showProviderCount && (
          <ProviderCountMessage
            count={providers?.length}
            testId="provider-count-message"
          />
        )}
      </div>

      <ElementClickTracker
        actionOutcome="Start Checkout"
        webElement={{
          elementType: ELEMENT_TYPES.BUTTON,
          location: 'Elevated Portal Roster',
          name: 'checkout',
        }}
      >
        <Button
          theme="deep-blue"
          disabled={isDisabled}
          onClick={onCheckout}
          className="w-full md:w-auto"
          data-qa-target="checkout-button"
        >
          {buttonText}
        </Button>
      </ElementClickTracker>
    </div>
  );
};

CheckoutSection.propTypes = {
  providers: PropTypes.array.isRequired,
  checkingOut: PropTypes.bool.isRequired,
  checkoutError: PropTypes.string.isRequired,
  onCheckout: PropTypes.func.isRequired,
};

export default CheckoutSection;
